// Staff Scheduling & Timesheets (Phase 3 Module 6)

import { supabase, isSupabaseConfigured } from "./supabase";

function assertLive() {
  if (!isSupabaseConfigured) throw new Error("Scheduling module requires live Supabase.");
}

const SHIFT_COLS =
  "id, organization_id, profile_id, classroom_id, starts_at, ends_at, role_label, notes, created_by, created_at, updated_at";

const ENTRY_COLS =
  "id, organization_id, profile_id, classroom_id, clock_in, clock_out, break_minutes, notes, source, approved_by, approved_at, created_at, updated_at";

// ---------------------------------------------------------------------------
// Shifts — admin CRUD
// ---------------------------------------------------------------------------

export async function listShifts({ from, to, organizationId }) {
  assertLive();
  let q = supabase
    .from("staff_shifts")
    .select(SHIFT_COLS)
    .order("starts_at", { ascending: true });
  if (organizationId) q = q.eq("organization_id", organizationId);
  if (from) q = q.gte("starts_at", from);
  if (to)   q = q.lte("starts_at", to);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

// Teacher-facing read-only schedule. RLS remains the source of truth; the
// profile filter keeps the request narrow and prevents the UI from depending
// on admin-only organization visibility.
export async function listMyShifts(profileId, { from, to } = {}) {
  assertLive();
  if (!profileId) return [];
  let q = supabase
    .from("staff_shifts")
    .select(`${SHIFT_COLS}, classrooms:classroom_id(id, name)`)
    .eq("profile_id", profileId)
    .order("starts_at", { ascending: true });
  if (from) q = q.gte("starts_at", from);
  if (to) q = q.lt("starts_at", to);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function createShift(payload) {
  assertLive();
  const { data, error } = await supabase
    .from("staff_shifts")
    .insert(payload)
    .select(SHIFT_COLS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateShift(id, patch) {
  assertLive();
  const { data, error } = await supabase
    .from("staff_shifts")
    .update(patch)
    .eq("id", id)
    .select(SHIFT_COLS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteShift(id) {
  assertLive();
  const { error } = await supabase.from("staff_shifts").delete().eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Teacher clock in / out
// ---------------------------------------------------------------------------

export async function getOpenEntry(profileId) {
  assertLive();
  const { data, error } = await supabase
    .from("staff_timesheet_entries")
    .select(ENTRY_COLS)
    .eq("profile_id", profileId)
    .is("clock_out", null)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function listMyEntries(profileId, { from, to, limit = 30 } = {}) {
  assertLive();
  let q = supabase
    .from("staff_timesheet_entries")
    .select(ENTRY_COLS)
    .eq("profile_id", profileId)
    .order("clock_in", { ascending: false })
    .limit(limit);
  if (from) q = q.gte("clock_in", from);
  if (to)   q = q.lte("clock_in", to);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function clockIn({ classroomId = null, notes = null } = {}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_clock_in", {
    p_classroom_id: classroomId,
    p_notes: notes,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function clockOut({ breakMinutes = 0, notes = null } = {}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_clock_out", {
    p_break_minutes: breakMinutes,
    p_notes: notes,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

// ---------------------------------------------------------------------------
// Timecard review — actual-time entries stay canonical; these RPCs provide
// the reviewed weekly envelope, correction history, and center-owned review
// threshold without making payroll or legal determinations in the client.
// ---------------------------------------------------------------------------

function unwrapRpc(data) {
  return Array.isArray(data) ? data[0] : data;
}

function numberOr(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function normalizeTimecardSnapshot(raw) {
  const value = unwrapRpc(raw) || {};
  const timecard = value.timecard || {};
  const policy = value.policy || {};
  const summary = value.summary || {};
  return {
    weekStart: value.week_start || null,
    weekEnd: value.week_end || null,
    timecard: {
      id: timecard.id || null,
      status: timecard.status || "open",
      submittedAt: timecard.submitted_at || null,
      submittedBy: timecard.submitted_by || null,
      approvedAt: timecard.approved_at || null,
      approvedBy: timecard.approved_by || null,
      approvalNote: timecard.approval_note || null,
      reopenedAt: timecard.reopened_at || null,
      reopenedBy: timecard.reopened_by || null,
      reopenReason: timecard.reopen_reason || null,
    },
    policy: {
      weeklyReviewThresholdMinutes: policy.weekly_review_threshold_minutes == null
        ? null
        : numberOr(policy.weekly_review_threshold_minutes, null),
      status: policy.status || "not_configured",
    },
    summary: {
      workedMinutes: numberOr(summary.worked_minutes),
      regularMinutes: summary.regular_minutes == null ? null : numberOr(summary.regular_minutes),
      overtimeMinutes: summary.overtime_minutes == null ? null : numberOr(summary.overtime_minutes),
      entryCount: numberOr(summary.entry_count),
      openEntryCount: numberOr(summary.open_entry_count),
      correctionCount: numberOr(summary.correction_count),
      maySubmit: !!summary.may_submit,
    },
    entries: Array.isArray(value.entries) ? value.entries : [],
    auditEvents: Array.isArray(value.audit_events) ? value.audit_events : [],
  };
}

export function normalizeTimecardReviewSummary(raw) {
  const value = unwrapRpc(raw) || {};
  const policy = value.policy || {};
  return {
    weekStart: value.week_start || null,
    policy: {
      weeklyReviewThresholdMinutes: policy.weekly_review_threshold_minutes == null
        ? null
        : numberOr(policy.weekly_review_threshold_minutes, null),
      status: policy.status || "not_configured",
    },
    timecards: (Array.isArray(value.timecards) ? value.timecards : []).map((row) => {
      const normalized = normalizeTimecardSnapshot({
        timecard: row.timecard,
        summary: row.summary,
        policy,
      });
      return {
        profileId: row.profile_id,
        fullName: row.full_name || "Staff member",
        email: row.email || "",
        ...normalized,
      };
    }),
  };
}

export async function getStaffTimecardSnapshot({ weekStart = null, profileId = null } = {}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_staff_timecard_snapshot", {
    p_week_start: weekStart,
    p_profile_id: profileId,
  });
  if (error) throw error;
  return normalizeTimecardSnapshot(data);
}

export async function getStaffTimecardReviewSummary({ weekStart = null } = {}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_staff_timecard_review_summary", {
    p_week_start: weekStart,
  });
  if (error) throw error;
  return normalizeTimecardReviewSummary(data);
}

export async function submitStaffTimecard({ weekStart }) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_submit_staff_timecard", {
    p_week_start: weekStart,
  });
  if (error) throw error;
  return normalizeTimecardSnapshot(data);
}

export async function approveStaffTimecard({ timecardReviewId, note = null }) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_approve_staff_timecard", {
    p_timecard_review_id: timecardReviewId,
    p_note: note,
  });
  if (error) throw error;
  return normalizeTimecardSnapshot(data);
}

export async function reopenStaffTimecard({ timecardReviewId, reason }) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_reopen_staff_timecard", {
    p_timecard_review_id: timecardReviewId,
    p_reason: reason,
  });
  if (error) throw error;
  return normalizeTimecardSnapshot(data);
}

export async function correctStaffTimesheetEntry({
  timesheetEntryId,
  clockIn,
  clockOut,
  breakMinutes,
  reason,
  notes = null,
}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_correct_staff_timesheet_entry", {
    p_timesheet_entry_id: timesheetEntryId,
    p_clock_in: clockIn,
    p_clock_out: clockOut,
    p_break_minutes: breakMinutes,
    p_reason: reason,
    p_notes: notes,
  });
  if (error) throw error;
  return normalizeTimecardSnapshot(data);
}

export async function updateStaffTimekeepingPolicy({ weeklyReviewThresholdMinutes = null } = {}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_upsert_staff_timekeeping_policy", {
    p_weekly_review_threshold_minutes: weeklyReviewThresholdMinutes,
  });
  if (error) throw error;
  const value = unwrapRpc(data) || {};
  return {
    weeklyReviewThresholdMinutes: value.weekly_review_threshold_minutes == null
      ? null
      : numberOr(value.weekly_review_threshold_minutes, null),
    status: value.status || "not_configured",
  };
}

// ---------------------------------------------------------------------------
// Staff time off — center-owned policies and a manual balance ledger.
// These wrappers deliberately never calculate accrual, eligibility, payroll,
// or paid-time rules in the client. The administrator's decision is the
// authority; the returned snapshot explains the resulting balance and any
// planned-shift conflict without changing either a shift or actual time.
// ---------------------------------------------------------------------------

function nullableNumber(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeTimeOffPolicy(raw = {}) {
  return {
    id: raw.id || null,
    code: raw.code || "",
    label: raw.label || "Time off",
    description: raw.description || null,
    balanceUnit: raw.balance_unit || "hours",
    tracksBalance: !!raw.tracks_balance,
    isActive: raw.is_active !== false,
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
  };
}

function normalizeTimeOffBalance(raw = {}) {
  return {
    profileId: raw.profile_id || null,
    policyId: raw.policy_id || null,
    policyCode: raw.policy_code || "",
    policyLabel: raw.policy_label || "Time off",
    balanceUnit: raw.balance_unit || "hours",
    tracksBalance: !!raw.tracks_balance,
    balanceAmount: nullableNumber(raw.balance_amount),
    balanceEntryCount: numberOr(raw.balance_entry_count),
  };
}

function normalizeTimeOffRequest(raw = {}) {
  return {
    id: raw.id || null,
    organizationId: raw.organization_id || null,
    profileId: raw.profile_id || null,
    profileName: raw.profile_name || "Staff member",
    profileEmail: raw.profile_email || "",
    policyId: raw.policy_id || null,
    policyCode: raw.policy_code || "",
    policyLabel: raw.policy_label || "Time off",
    balanceUnit: raw.balance_unit || "hours",
    tracksBalance: !!raw.tracks_balance,
    startsAt: raw.starts_at || null,
    endsAt: raw.ends_at || null,
    requestedAmount: nullableNumber(raw.requested_amount),
    requestNote: raw.request_note || null,
    status: raw.status || "pending",
    approvedAt: raw.approved_at || null,
    approvedBy: raw.approved_by || null,
    declinedAt: raw.declined_at || null,
    declinedBy: raw.declined_by || null,
    decisionNote: raw.decision_note || null,
    cancelledAt: raw.cancelled_at || null,
    cancelledBy: raw.cancelled_by || null,
    cancelNote: raw.cancel_note || null,
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
    scheduledShiftConflictCount: numberOr(raw.scheduled_shift_conflict_count),
  };
}

export function normalizeStaffTimeOffSnapshot(raw) {
  const value = unwrapRpc(raw) || {};
  return {
    status: value.status || "not_configured",
    policies: (Array.isArray(value.policies) ? value.policies : []).map(normalizeTimeOffPolicy),
    balances: (Array.isArray(value.balances) ? value.balances : []).map(normalizeTimeOffBalance),
    requests: (Array.isArray(value.requests) ? value.requests : []).map(normalizeTimeOffRequest),
    staff: (Array.isArray(value.staff) ? value.staff : []).map((row) => ({
      id: row.id || null,
      fullName: row.full_name || "Staff member",
      email: row.email || "",
    })),
  };
}

export async function getStaffTimeOffSnapshot() {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_staff_time_off_snapshot");
  if (error) throw error;
  return normalizeStaffTimeOffSnapshot(data);
}

export async function getStaffTimeOffAdminSnapshot({ from = null, to = null } = {}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_staff_time_off_admin_snapshot", {
    p_from: from,
    p_to: to,
  });
  if (error) throw error;
  return normalizeStaffTimeOffSnapshot(data);
}

export async function upsertStaffTimeOffPolicy({
  policyId = null,
  code,
  label,
  description = null,
  balanceUnit = "hours",
  tracksBalance = false,
  isActive = true,
}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_upsert_staff_time_off_policy", {
    p_policy_id: policyId,
    p_code: code,
    p_label: label,
    p_description: description,
    p_balance_unit: balanceUnit,
    p_tracks_balance: tracksBalance,
    p_is_active: isActive,
  });
  if (error) throw error;
  return normalizeTimeOffPolicy(unwrapRpc(data));
}

export async function adjustStaffTimeOffBalance({
  profileId,
  policyId,
  amount,
  effectiveOn,
  reason,
}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_adjust_staff_time_off_balance", {
    p_profile_id: profileId,
    p_policy_id: policyId,
    p_amount: amount,
    p_effective_on: effectiveOn,
    p_reason: reason,
  });
  if (error) throw error;
  return normalizeTimeOffBalance(unwrapRpc(data));
}

export async function createStaffTimeOffRequest({
  policyId,
  startsAt,
  endsAt,
  requestedAmount,
  requestNote = null,
}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_create_staff_time_off_request", {
    p_policy_id: policyId,
    p_starts_at: startsAt,
    p_ends_at: endsAt,
    p_requested_amount: requestedAmount,
    p_request_note: requestNote,
  });
  if (error) throw error;
  return normalizeTimeOffRequest(unwrapRpc(data));
}

export async function decideStaffTimeOffRequest({ requestId, decision, decisionNote = null }) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_decide_staff_time_off_request", {
    p_request_id: requestId,
    p_decision: decision,
    p_decision_note: decisionNote,
  });
  if (error) throw error;
  return normalizeTimeOffRequest(unwrapRpc(data));
}

export async function cancelStaffTimeOffRequest({ requestId, cancelNote = null }) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_cancel_staff_time_off_request", {
    p_request_id: requestId,
    p_cancel_note: cancelNote,
  });
  if (error) throw error;
  return normalizeTimeOffRequest(unwrapRpc(data));
}

// ---------------------------------------------------------------------------
// Recurring planned-shift templates — a center administrator explicitly
// materializes an upcoming range into ordinary `staff_shifts`. These helpers
// deliberately do not synthesize a shift in the browser, update actual time,
// or treat coverage output as a staffing decision.
// ---------------------------------------------------------------------------

function normalizeStaffShiftTemplateSlot(raw = {}) {
  return {
    id: raw.id || null,
    profileId: raw.profile_id || null,
    profileName: raw.profile_name || "Staff member",
    profileEmail: raw.profile_email || "",
    classroomId: raw.classroom_id || null,
    classroomName: raw.classroom_name || "Classroom",
    weekday: raw.weekday == null || raw.weekday === ""
      ? null
      : (Number.isInteger(Number(raw.weekday)) ? Number(raw.weekday) : null),
    startsAt: raw.starts_at || null,
    endsAt: raw.ends_at || null,
    roleLabel: raw.role_label || null,
    notes: raw.notes || null,
    isActive: raw.is_active !== false,
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
  };
}

function normalizeStaffShiftTemplate(raw = {}) {
  return {
    id: raw.id || null,
    name: raw.name || "Weekly template",
    isActive: raw.is_active !== false,
    slotCount: numberOr(raw.slot_count),
    activeSlotCount: numberOr(raw.active_slot_count),
    slots: (Array.isArray(raw.slots) ? raw.slots : []).map(normalizeStaffShiftTemplateSlot),
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
  };
}

function normalizeStaffShiftTemplateApplication(raw = {}) {
  return {
    id: raw.id || null,
    templateId: raw.template_id || null,
    templateName: raw.template_name || "Weekly template",
    rangeStart: raw.range_start || null,
    rangeEnd: raw.range_end || null,
    createdShiftCount: numberOr(raw.created_shift_count),
    skippedExistingCount: numberOr(raw.skipped_existing_count),
    skippedConflictCount: numberOr(raw.skipped_conflict_count),
    skippedTimeOffCount: numberOr(raw.skipped_time_off_count),
    appliedAt: raw.applied_at || null,
    appliedByName: raw.applied_by_name || "Administrator",
  };
}

export function normalizeStaffShiftTemplateSnapshot(raw) {
  const value = unwrapRpc(raw) || {};
  return {
    status: value.status || "not_configured",
    windowStart: value.window_start || null,
    windowEnd: value.window_end || null,
    templates: (Array.isArray(value.templates) ? value.templates : []).map(normalizeStaffShiftTemplate),
    applications: (Array.isArray(value.applications) ? value.applications : []).map(normalizeStaffShiftTemplateApplication),
    generatedShiftCount: numberOr(value.generated_shift_count),
  };
}

export async function getStaffShiftTemplateSnapshot({ from = null, to = null } = {}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_staff_shift_templates_snapshot", {
    p_from: from,
    p_to: to,
  });
  if (error) throw error;
  return normalizeStaffShiftTemplateSnapshot(data);
}

export async function upsertStaffShiftTemplate({ templateId = null, name, isActive = true }) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_upsert_staff_shift_template", {
    p_template_id: templateId,
    p_name: name,
    p_is_active: isActive,
  });
  if (error) throw error;
  return normalizeStaffShiftTemplate(unwrapRpc(data));
}

export async function upsertStaffShiftTemplateSlot({
  slotId = null,
  templateId,
  profileId,
  classroomId,
  weekday,
  startsAt,
  endsAt,
  roleLabel = null,
  notes = null,
  isActive = true,
}) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_upsert_staff_shift_template_slot", {
    p_slot_id: slotId,
    p_template_id: templateId,
    p_profile_id: profileId,
    p_classroom_id: classroomId,
    p_weekday: weekday == null || weekday === "" ? null : Number(weekday),
    p_starts_at: startsAt || null,
    p_ends_at: endsAt || null,
    p_role_label: roleLabel,
    p_notes: notes,
    p_is_active: isActive,
  });
  if (error) throw error;
  return normalizeStaffShiftTemplateSlot(unwrapRpc(data));
}

export async function applyStaffShiftTemplate({ templateId, rangeStart, rangeEnd }) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_apply_staff_shift_template", {
    p_template_id: templateId,
    p_range_start: rangeStart,
    p_range_end: rangeEnd,
  });
  if (error) throw error;
  return normalizeStaffShiftTemplateApplication(unwrapRpc(data));
}

// ---------------------------------------------------------------------------
// Admin — week summary per staff
// ---------------------------------------------------------------------------

export async function getStaffWeekSummary({ weekStart, weekEnd }) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_staff_week_summary", {
    p_week_start: weekStart,
    p_week_end: weekEnd,
  });
  if (error) throw error;
  return (data || []).map((r) => ({
    profile_id: r.out_profile_id,
    full_name: r.out_full_name,
    scheduled_minutes: r.out_scheduled_minutes ?? 0,
    worked_minutes: r.out_worked_minutes ?? 0,
    open_entry: !!r.out_open_entry,
  }));
}

export async function listAdminEntriesForWeek({ weekStart, weekEnd }) {
  assertLive();
  // Disambiguate the profiles relationship — staff_timesheet_entries has
  // TWO FKs to profiles (profile_id + approved_by), so we must spell out
  // `profile_id` as the embedding target.
  const { data, error } = await supabase
    .from("staff_timesheet_entries")
    .select(
      `${ENTRY_COLS}, profiles:profile_id(id, full_name, email), classrooms:classroom_id(id, name)`,
    )
    .gte("clock_in", weekStart)
    .lt("clock_in", weekEnd)
    .order("clock_in", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getClassroomRatioByDay({ weekStart, weekEnd }) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_classroom_ratio_by_day", {
    p_week_start: weekStart,
    p_week_end: weekEnd,
  });
  if (error) throw error;
  return (data || []).map((r) => ({
    classroom_id: r.out_classroom_id,
    classroom_name: r.out_classroom_name,
    day: r.out_day,
    required_staff_ratio: r.out_required_staff_ratio,
    children_present: r.out_children_present ?? 0,
    staff_on_clock: r.out_staff_on_clock ?? 0,
    ratio_state: r.out_ratio_state,
  }));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function startOfWeek(d = new Date()) {
  const out = new Date(d);
  const day = out.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  out.setDate(out.getDate() + diff);
  out.setHours(0, 0, 0, 0);
  return out;
}

export function endOfWeek(d = new Date()) {
  const start = startOfWeek(d);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return end;
}

export function localDateKey(value = new Date()) {
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function timecardStatusLabel(status) {
  switch (status) {
    case "submitted":
      return "Awaiting review";
    case "approved":
      return "Approved";
    case "reopened":
      return "Needs review";
    default:
      return "Open";
  }
}

export function timeOffStatusLabel(status) {
  switch (status) {
    case "approved":
      return "Approved";
    case "declined":
      return "Declined";
    case "cancelled":
      return "Cancelled";
    default:
      return "Awaiting review";
  }
}

export function formatTimeOffAmount(amount, unit = "hours") {
  const value = nullableNumber(amount);
  if (value == null) return "Not recorded";
  const normalized = Math.abs(value) === 1 ? unit.replace(/s$/, "") : unit;
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)} ${normalized}`;
}

export function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return "0m";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function computeEntryMinutes(entry, now = new Date()) {
  if (!entry) return 0;
  const start = new Date(entry.clock_in).getTime();
  const end = entry.clock_out ? new Date(entry.clock_out).getTime() : now.getTime();
  const ms = Math.max(0, end - start);
  return Math.max(0, Math.round(ms / 60000) - (entry.break_minutes || 0));
}
